// Feedback > By Product — "what do customers actually say about this one thing?"
//
// Two things this screen refuses to do, both learned from the lobster claws
// question that prompted it:
//
//  - It never shows the average on its own. A feedback belongs to an ORDER, and
//    most orders carry several products, so "orders containing X scored 4.65"
//    is not X's score. The baseline sits next to it, the count of other items on
//    the order sits on every row, and sole-product orders get their own figure.
//
//  - It puts comment search level with the averages, not buried. On the very
//    first product anyone ran, the scores said fine and four comments said
//    otherwise — and one of those four rated the product 4 out of 5.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink, MessageSquare, RefreshCw, Search, X } from 'lucide-react';
import { productFeedbackApi } from '../services/api';

const RANGES = [
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '60', label: 'Last 60 days', days: 60 },
  { id: '90', label: 'Last 90 days', days: 90 },
  { id: '365', label: 'Last 12 months', days: 365 },
  { id: 'all', label: 'All time', days: null },
  { id: 'custom', label: 'Custom', days: null },
];

const iso = (d) => d.toISOString().slice(0, 10);
function agoIso(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return iso(d);
}

// A DATE-shaped string must not go through new Date() — parsed as UTC then
// rendered in Eastern it prints the day before.
function fmtDay(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(s);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

function Score({ n }) {
  if (n === null || n === undefined) {
    return <span style={{ color: 'var(--gray-400, #98a2ad)', fontSize: 12 }}>—</span>;
  }
  const color = n <= 2 ? '#b3261e' : n === 3 ? '#b26a00' : '#1e7a3c';
  return <span style={{ fontWeight: 700, color }}>{n}</span>;
}

// Highlight the searched term inside the comment so the reason a row matched is
// visible rather than left to the reader to spot.
function Comment({ text, term }) {
  if (!text) return <span style={{ color: 'var(--gray-400, #98a2ad)' }}>No comment left.</span>;
  const q = String(term || '').trim();
  if (!q) return <>{text}</>;
  const parts = [];
  const lower = text.toLowerCase();
  const needle = q.toLowerCase();
  let i = 0;
  while (i < text.length) {
    const at = lower.indexOf(needle, i);
    if (at === -1) { parts.push(text.slice(i)); break; }
    if (at > i) parts.push(text.slice(i, at));
    parts.push(<mark key={at} style={{ background: 'var(--peach)', padding: '0 2px' }}>{text.slice(at, at + q.length)}</mark>);
    i = at + q.length;
  }
  return <>{parts}</>;
}

export default function ProductFeedback({ navigateTo }) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [picked, setPicked] = useState(null);
  const [rangeId, setRangeId] = useState('365');
  const [customStart, setCustomStart] = useState(agoIso(365));
  const [customEnd, setCustomEnd] = useState(iso(new Date()));
  const [soleOnly, setSoleOnly] = useState(false);
  const [commentQuery, setCommentQuery] = useState('');
  const [appliedComment, setAppliedComment] = useState('');
  const [report, setReport] = useState(null);
  const [repeat, setRepeat] = useState(null);
  const [tab, setTab] = useState('feedback');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const searchTimer = useRef(null);

  const windowFor = useCallback(() => {
    const r = RANGES.find((x) => x.id === rangeId);
    if (rangeId === 'custom') return { start: customStart, end: customEnd };
    if (!r || r.days === null) return { start: null, end: null };
    return { start: agoIso(r.days), end: iso(new Date()) };
  }, [rangeId, customStart, customEnd]);

  // Product picker, debounced.
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const d = await productFeedbackApi('products', { search, limit: 40 });
        setProducts(d.products || []);
      } catch (e) { setErr(e.message); }
    }, 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search]);

  const run = useCallback(async (product, commentTerm) => {
    if (!product) return;
    setLoading(true); setErr('');
    const w = windowFor();
    try {
      const [a, b] = await Promise.all([
        productFeedbackApi('report', {
          masterId: product.catalogId, start: w.start, end: w.end,
          soleOnly, commentQuery: commentTerm ?? appliedComment,
        }),
        productFeedbackApi('repeat', { masterId: product.catalogId, start: w.start, end: w.end }),
      ]);
      setReport(a.report);
      setRepeat(b.repeat);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [windowFor, soleOnly, appliedComment]);

  useEffect(() => { if (picked) run(picked); /* eslint-disable-next-line */ }, [picked, rangeId, customStart, customEnd, soleOnly, appliedComment]);

  const s = report?.summary;
  const sole = report?.soleProductSummary;
  const base = report?.baseline;
  const delta = s?.avg != null && base?.avg != null ? s.avg - base.avg : null;

  return (
    <div>
      <div className="breadcrumb">
        <a href="#feedback">Feedback</a><span> / </span><span>By Product</span>
      </div>

      <h1 style={{ margin: '0 0 4px', color: 'var(--navy)' }}>Feedback by Product</h1>
      <p style={{ margin: '0 0 16px', color: '#5a6672', maxWidth: 760 }}>
        Every feedback response left on an order that contained a given product, plus who has bought
        it more than once.
      </p>

      {err && (
        <div className="card" style={{ borderLeft: '4px solid #b3261e', marginBottom: 16 }}>
          <div className="card-body" style={{ color: '#b3261e' }}>
            <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />{err}
          </div>
        </div>
      )}

      {/* ── product picker ── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body">
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Product</label>
            <div style={{ position: 'relative', maxWidth: 560 }}>
              <Search size={15} style={{ position: 'absolute', left: 10, top: 11, color: '#98a2ad' }} />
              <input
                className="form-input" style={{ paddingLeft: 32 }}
                placeholder="Start typing — lobster claws, crab cakes, snow crab…"
                value={picked ? picked.name : search}
                onChange={(e) => { setPicked(null); setReport(null); setRepeat(null); setSearch(e.target.value); }}
              />
              {picked && (
                <button
                  className="btn btn-ghost"
                  style={{ position: 'absolute', right: 4, top: 4 }}
                  onClick={() => { setPicked(null); setReport(null); setRepeat(null); setSearch(''); }}
                ><X size={14} /></button>
              )}
            </div>
          </div>

          {!picked && !!products.length && (
            <div style={{ marginTop: 10, maxHeight: 260, overflowY: 'auto', border: '1px solid #e2e6ea', borderRadius: 4 }}>
              {products.map((p) => (
                <div
                  key={p.catalogId}
                  onClick={() => { setPicked(p); setProducts([]); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f2f4', display: 'flex', justifyContent: 'space-between', gap: 12 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#f6f8fa'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#5a6672' }}>
                      {p.orders.toLocaleString()} orders
                      {p.skuCount > 1 && ` · ${p.skuCount} sizes/bundles rolled up`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {picked && (
        <>
          {/* ── filters ── */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label">Date range</label>
                <select className="form-select" style={{ maxWidth: 170 }} value={rangeId} onChange={(e) => setRangeId(e.target.value)}>
                  {RANGES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                </select>
              </div>
              {rangeId === 'custom' && (
                <>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">From</label>
                    <input type="date" className="form-input" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">To</label>
                    <input type="date" className="form-input" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                  </div>
                </>
              )}
              <div className="form-group" style={{ margin: 0, minWidth: 240 }}>
                <label className="form-label">Search the comments</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    className="form-input" placeholder="e.g. taste, rubbery, thawed"
                    value={commentQuery}
                    onChange={(e) => setCommentQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setAppliedComment(commentQuery); }}
                  />
                  <button className="btn btn-outline" onClick={() => setAppliedComment(commentQuery)}>Find</button>
                  {appliedComment && (
                    <button className="btn btn-ghost" onClick={() => { setCommentQuery(''); setAppliedComment(''); }}><X size={14} /></button>
                  )}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--navy)', paddingBottom: 8 }}>
                <input type="checkbox" checked={soleOnly} onChange={(e) => setSoleOnly(e.target.checked)} />
                Only orders where this was the single item
              </label>
              <button className="btn btn-outline" onClick={() => run(picked)} disabled={loading} style={{ marginBottom: 2 }}>
                <RefreshCw size={14} style={{ marginRight: 6 }} />Refresh
              </button>
            </div>
          </div>

          {/* ── the caveat, stated once, in plain words ── */}
          <div className="card" style={{ marginBottom: 16, borderLeft: '4px solid var(--orange)' }}>
            <div className="card-body" style={{ fontSize: 13, color: '#3d4a56' }}>
              <strong>Read the comments, not just the average.</strong> Customers give one product
              score for the whole order, so if this product shipped alongside anything else the score
              covers all of it. Someone can be unhappy with this item and still rate the order 4 or 5
              because the rest was good. The <em>Items on order</em> column and the single-item filter
              above are there for that reason.
            </div>
          </div>

          {/* ── summary ── */}
          {s && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-body" style={{ display: 'flex', gap: 34, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>Responses</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{s.responses}</div>
                  {s.thin && <div style={{ fontSize: 12, color: '#b26a00' }}>too few to read a trend into</div>}
                </div>
                <div>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>Avg product score</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{s.avg ?? '—'}</div>
                  <div style={{ fontSize: 12, color: '#5a6672' }}>
                    site-wide {base?.avg ?? '—'}
                    {delta !== null && (
                      <span style={{ color: delta >= 0 ? '#1e7a3c' : '#b3261e', fontWeight: 700 }}>
                        {' '}({delta >= 0 ? '+' : ''}{delta.toFixed(2)})
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>Scored 3 or lower</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{s.lowPct ?? '—'}%</div>
                  <div style={{ fontSize: 12, color: '#5a6672' }}>site-wide {base?.lowPct ?? '—'}%</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>Single-item orders</div>
                  <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{sole?.avg ?? '—'}</div>
                  <div style={{ fontSize: 12, color: '#5a6672' }}>
                    {sole?.scored ? `from ${sole.scored} response${sole.scored === 1 ? '' : 's'} about this alone` : 'none in this window'}
                  </div>
                </div>
                {repeat?.totals && (
                  <div>
                    <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em', color: '#5a6672' }}>Bought again</div>
                    <div style={{ fontSize: 30, fontWeight: 700, color: 'var(--navy)' }}>{repeat.totals.repeatPct ?? '—'}%</div>
                    <div style={{ fontSize: 12, color: '#5a6672' }}>
                      {repeat.totals.repeaters} of {repeat.totals.customers} customers
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── tabs ── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className={tab === 'feedback' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('feedback')}>
              <MessageSquare size={14} style={{ marginRight: 6 }} />Feedback ({report?.rows?.length ?? 0})
            </button>
            <button className={tab === 'repeat' ? 'btn btn-primary' : 'btn btn-outline'} onClick={() => setTab('repeat')}>
              Repeat buyers ({repeat?.customers?.length ?? 0})
            </button>
          </div>

          {tab === 'feedback' && (
            <div className="card">
              <div className="card-body">
                {appliedComment && (
                  <div style={{ marginBottom: 10, fontSize: 13, color: '#5a6672' }}>
                    Showing responses whose comment mentions <strong>“{appliedComment}”</strong>
                    {report?.commentMatches !== null && ` — ${report.commentMatches} of ${report.summary.responses}`}
                  </div>
                )}
                {!report?.rows?.length && !loading && (
                  <div style={{ color: '#5a6672' }}>No feedback matches this product and window.</div>
                )}
                {!!report?.rows?.length && (
                  <table className="product-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th>Order</th><th>Customer</th><th>Date</th>
                        <th style={{ textAlign: 'center' }}>Product</th>
                        <th style={{ textAlign: 'center' }}>Pack</th>
                        <th style={{ textAlign: 'center' }}>Deliv</th>
                        <th style={{ textAlign: 'center' }}>Overall</th>
                        <th style={{ textAlign: 'center' }}>Items on order</th>
                        <th>Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((r) => (
                        <tr key={r.feedId}>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            {(r.bcOrderId || r.orderId) ? (
                              <a
                                className="order-link"
                                style={{ color: 'var(--orange)', cursor: 'pointer' }}
                                onClick={() => navigateTo && navigateTo('order-details', String(r.bcOrderId || r.orderId))}
                              >
                                {r.bcOrderId || r.orderId}
                                <ExternalLink size={11} style={{ marginLeft: 4, verticalAlign: -1 }} />
                              </a>
                            ) : '—'}
                            {!r.bcOrderId && r.orderId && (
                              <div style={{ fontSize: 10, color: '#98a2ad' }}>internal #</div>
                            )}
                          </td>
                          <td>
                            <div>{r.customer || '—'}</div>
                            {r.where && <div style={{ fontSize: 11, color: '#5a6672' }}>{r.where}</div>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDay(r.feedDate)}</td>
                          <td style={{ textAlign: 'center' }}><Score n={r.scores.product} /></td>
                          <td style={{ textAlign: 'center' }}><Score n={r.scores.packaging} /></td>
                          <td style={{ textAlign: 'center' }}><Score n={r.scores.delivery} /></td>
                          <td style={{ textAlign: 'center' }}><Score n={r.scores.overall} /></td>
                          <td style={{ textAlign: 'center' }}>
                            {r.soleProduct ? (
                              <span style={{ background: '#e7f4ec', color: '#1e7a3c', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                this only
                              </span>
                            ) : (
                              <span style={{ color: '#5a6672', fontSize: 12 }}>{r.linesOnOrder} items</span>
                            )}
                          </td>
                          <td style={{ maxWidth: 380, fontSize: 13 }}>
                            <Comment text={r.comment} term={appliedComment} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {report?.truncated && (
                  <div style={{ marginTop: 10, fontSize: 12, color: '#b26a00' }}>
                    Showing the most recent {report.rows.length}. Narrow the date range to see the rest.
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'repeat' && (
            <div className="card">
              <div className="card-body">
                <div style={{ marginBottom: 12, fontSize: 13, color: '#3d4a56' }}>
                  A reorder counts as a <strong>separate day</strong>, not a separate order. People
                  send several gift boxes at once here, and counting those as repeat purchases would
                  turn one afternoon into a loyal customer.
                </div>
                {!repeat?.customers?.length && !loading && (
                  <div style={{ color: '#5a6672' }}>Nobody has bought this in the selected window.</div>
                )}
                {!!repeat?.customers?.length && (
                  <table className="product-table" style={{ width: '100%' }}>
                    <thead>
                      <tr><th>Customer</th><th>Email</th><th style={{ textAlign: 'center' }}>Times bought</th><th>First</th><th>Most recent</th></tr>
                    </thead>
                    <tbody>
                      {repeat.customers.map((c) => (
                        <tr key={c.customerId}>
                          <td style={{ fontWeight: 600 }}>{c.name}</td>
                          <td style={{ fontSize: 12, color: '#5a6672' }}>{c.email}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: c.buys >= 2 ? '#1e7a3c' : '#5a6672' }}>{c.buys}</td>
                          <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDay(c.firstBuy)}</td>
                          <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDay(c.lastBuy)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
